import { useCallback, useEffect, useState, type RefObject } from "react"

/**
 * Wraps the browser Fullscreen API for a specific element.
 *
 * - `isFullscreen` mirrors `document.fullscreenElement === ref.current`, so
 *   pressing Esc (the browser-driven exit) keeps React state in sync.
 * - `enter()` / `exit()` are guarded — calling them in the wrong state is a
 *   no-op, never a thrown error.
 * - Includes WebKit prefix fallbacks for older Safari.
 *
 * Designed for the video / document share layouts: the caller attaches the
 * ref to the *content* element they want fullscreened (not the meeting
 * shell) so the participant rail / control bar are excluded.
 */

interface VendorDocument extends Document {
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => Promise<void>
}

interface VendorElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>
}

const getActiveFullscreenElement = (): Element | null => {
  const doc = document as VendorDocument
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null
}

const requestFs = async (el: HTMLElement): Promise<void> => {
  const vendor = el as VendorElement
  if (typeof el.requestFullscreen === "function") {
    return el.requestFullscreen()
  }
  if (typeof vendor.webkitRequestFullscreen === "function") {
    return vendor.webkitRequestFullscreen()
  }
  return Promise.reject(new Error("Fullscreen API not supported"))
}

/**
 * In-page fullscreen, for where the Fullscreen API does not reach.
 *
 * iPhone browsers (all of them WebKit) cannot fullscreen an element at all —
 * only a <video>, and only by handing it to the native player. That player
 * brings its own pause/resume and a minimise button that floats the video off
 * the page, leaving the stage blank and reading as "screen sharing has
 * stopped". So an element that cannot be fullscreened is pinned over the
 * viewport instead (the attribute's CSS is in index.css) and keeps our own
 * controls. The emulator never shows this: device mode only swaps the user
 * agent, and desktop Chrome fullscreens any element.
 *
 * An attribute rather than a class: React rewrites `className` whenever the
 * owner's classes change, which would silently drop us out of fullscreen.
 */
export const PSEUDO_FULLSCREEN_ATTR = "data-vc-pseudo-fullscreen"
/** Fired on `document` whenever an element enters or leaves in-page fullscreen. */
export const PSEUDO_FULLSCREEN_EVENT = "vc:pseudofullscreenchange"

/** Whether the real Fullscreen API can take this element. */
export const canFullscreenElement = (el: HTMLElement): boolean =>
  typeof el.requestFullscreen === "function" ||
  typeof (el as VendorElement).webkitRequestFullscreen === "function"

/** The element currently pinned in-page, or null. */
export const pseudoFullscreenElement = (): HTMLElement | null =>
  typeof document === "undefined"
    ? null
    : document.querySelector<HTMLElement>(`[${PSEUDO_FULLSCREEN_ATTR}]`)

/**
 * Marks every ancestor of the pinned element while it is pinned.
 *
 * `position: fixed` is only relative to the viewport when no ancestor has a
 * transform, translate, filter, will-change or contain — Tailwind's
 * `translate-*` utilities alone are enough — and a positioned ancestor with a
 * lower z-index than its siblings would leave the control bar painted on top.
 * The ancestor CSS (index.css) lifts both for as long as the pin lasts.
 */
const PSEUDO_FULLSCREEN_ANCESTOR_ATTR = "data-vc-pseudo-fullscreen-ancestor"

const markAncestors = (el: HTMLElement, on: boolean): void => {
  if (!on) {
    document
      .querySelectorAll(`[${PSEUDO_FULLSCREEN_ANCESTOR_ATTR}]`)
      .forEach((a) => a.removeAttribute(PSEUDO_FULLSCREEN_ANCESTOR_ATTR))
    return
  }
  for (let a = el.parentElement; a && a !== document.body; a = a.parentElement) {
    a.setAttribute(PSEUDO_FULLSCREEN_ANCESTOR_ATTR, "")
  }
}

/** Pin or unpin an element over the viewport. One at a time, like the real API. */
export function setPseudoFullscreen(el: HTMLElement, on: boolean): void {
  if (on === el.hasAttribute(PSEUDO_FULLSCREEN_ATTR)) return
  const previous = pseudoFullscreenElement()
  if (previous) {
    previous.removeAttribute(PSEUDO_FULLSCREEN_ATTR)
    markAncestors(previous, false)
  }
  if (on) {
    el.setAttribute(PSEUDO_FULLSCREEN_ATTR, "")
    markAncestors(el, true)
  }
  document.dispatchEvent(new Event(PSEUDO_FULLSCREEN_EVENT))
}

const exitFs = async (): Promise<void> => {
  const doc = document as VendorDocument
  if (typeof document.exitFullscreen === "function") {
    return document.exitFullscreen()
  }
  if (typeof doc.webkitExitFullscreen === "function") {
    return doc.webkitExitFullscreen()
  }
}

/**
 * One button's worth of fullscreen for an element the caller does not own a
 * hook for (a video tile's maximize): leave if anything is fullscreen, else
 * enter — through the API where it exists, pinned in-page where it does not.
 *
 * iPhone used to get the tile's <video> handed to the native player instead,
 * which brought its pause/resume and a minimise button that floated the video
 * off the page.
 */
export function toggleElementFullscreen(el: HTMLElement): void {
  if (el.hasAttribute(PSEUDO_FULLSCREEN_ATTR)) {
    setPseudoFullscreen(el, false)
    return
  }
  if (getActiveFullscreenElement()) {
    void exitFs().catch(() => {})
    return
  }
  if (!canFullscreenElement(el)) {
    setPseudoFullscreen(el, true)
    return
  }
  void requestFs(el).catch(() => {})
}

export interface UseFullscreenResult {
  isFullscreen: boolean
  enter: () => void
  exit: () => void
  toggle: () => void
}

export function useFullscreen(
  ref: RefObject<HTMLElement | null>,
): UseFullscreenResult {
  const [isFullscreen, setIsFullscreen] = useState(false)

  // Re-resolve on every fullscreenchange — covers user-driven Esc, F11,
  // and programmatic transitions in other parts of the app.
  useEffect(() => {
    const sync = () => {
      const target = ref.current
      setIsFullscreen(
        target !== null &&
          (getActiveFullscreenElement() === target || target.hasAttribute(PSEUDO_FULLSCREEN_ATTR)),
      )
    }
    sync()
    document.addEventListener("fullscreenchange", sync)
    document.addEventListener("webkitfullscreenchange", sync)
    document.addEventListener(PSEUDO_FULLSCREEN_EVENT, sync)
    return () => {
      document.removeEventListener("fullscreenchange", sync)
      document.removeEventListener("webkitfullscreenchange", sync)
      document.removeEventListener(PSEUDO_FULLSCREEN_EVENT, sync)
    }
  }, [ref])

  // Defensive: if the host unmounts while still owning the fullscreen layer
  // (e.g. the consumer's parent flipped `presentingVideo` back to null),
  // make sure we drop out of fullscreen so the meeting chrome doesn't stay
  // hidden. Browsers exit on DOM removal automatically, but doing this
  // explicitly is cheap and removes a race.
  useEffect(() => {
    // Captured now: by cleanup time the ref is already null, and an element
    // left pinned in-page would keep covering the meeting after it unmounts.
    const el = ref.current
    return () => {
      if (el?.hasAttribute(PSEUDO_FULLSCREEN_ATTR)) setPseudoFullscreen(el, false)
      if (el && getActiveFullscreenElement() === el) {
        void exitFs().catch(() => {})
      }
    }
  }, [ref])

  const enter = useCallback(() => {
    const el = ref.current
    if (!el || getActiveFullscreenElement() === el || el.hasAttribute(PSEUDO_FULLSCREEN_ATTR)) return
    if (!canFullscreenElement(el)) {
      setPseudoFullscreen(el, true)
      return
    }
    requestFs(el)
      .then(() => {
        // Put keyboard focus on the thing that just went fullscreen.
        //
        // Whatever was focused before usually still is — and if that was an
        // embedded player or document, its iframe keeps every keystroke. The
        // shortcuts listen on `window`, so nothing in this document hears them
        // and the meeting appears to have stopped responding to the keyboard
        // altogether. Browsers do not move focus on their own here.
        const target = ref.current
        if (!target) return
        const active = document.activeElement
        if (active instanceof HTMLIFrameElement) active.blur()
        if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1")
        target.focus({ preventScroll: true })
      })
      .catch((err) => {
        console.warn("[useFullscreen] enter failed", err)
      })
  }, [ref])

  const exit = useCallback(() => {
    const el = ref.current
    if (el?.hasAttribute(PSEUDO_FULLSCREEN_ATTR)) {
      setPseudoFullscreen(el, false)
      return
    }
    if (!getActiveFullscreenElement()) return
    exitFs().catch((err) => {
      console.warn("[useFullscreen] exit failed", err)
    })
  }, [ref])

  const toggle = useCallback(() => {
    if (isFullscreen) exit()
    else enter()
  }, [isFullscreen, enter, exit])

  return { isFullscreen, enter, exit, toggle }
}
